import {
	AssetTreePicker,
	ErrorModal,
	useUserContext,
} from "@dexteel/mesf-core";
import {
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogContentText,
	DialogTitle,
	IconButton,
	Paper,
	Snackbar,
	Typography,
} from "@mui/material";
import Grid2 from "@mui/material/Grid2";
import AddBoxIcon from "@mui/icons-material/AddBox";
import FormatListBulletedSharpIcon from "@mui/icons-material/FormatListBulletedSharp";
import ScheduleIcon from "@mui/icons-material/Schedule";
import Tooltip from "@mui/material/Tooltip";
import { Alert } from "@mui/lab";
import { globalDefaultColDef } from "@setup/ag-grid/mesf-theme";
import type {
	ColDef,
	DefaultMenuItem,
	EditableCallbackParams,
	GetContextMenuItemsParams,
	ICellRendererParams,
	MenuItemDef,
} from "ag-grid-community";
import "ag-grid-enterprise";
import { AgGridReact } from "ag-grid-react";
import type React from "react";
import {
	useCallback,
	useEffect,
	useMemo,
	useRef,
	useState,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import CustomLoadingOverlay from "@/components/loaders/LazyLoading/LazyLoading";
import { CreateTemplate } from "./components/CreateTemplate";
import { EditTemplate } from "./components/EditTemplate";
import { Filters } from "./components/filters";
import { useSearchParamsToReducer } from "./components/hooks/useSearchParamsToReducer";
import { TableTemplateResoures } from "./components/template-resources/table-template-resoures";
import { useTemplatesContext } from "./context/templatesContext";
import { useInitializeTemplate } from "./hooks/useInitializeTemplate";
import { useLoadPeriodPerDay } from "./hooks/useLoadPeriodPerDay";
import { Template } from "./models/Template";
import { useQueryClient } from "@tanstack/react-query";
import { TEMPLATES_QUERY_KEYS, useTemplates } from "./hooks/useTemplatesQueries";
import {
	useCreatePMOrder,
	useDeleteTemplate,
} from "./hooks/useTemplatesMutations";
import { getCraftLetter } from "@/utils/craftLetter";

export const Templates = () => {
	const {
		state: { defaultAreaId },
	} = useUserContext();
	const {
		state: {
			searchAssetIdToTable,
			searchText,
			showDownDay,
			templateIdToDelete,
			rowSelectedTemplate,
			rowSelectedTemplate: { AssetId, TemplateId, Title, AssetName, TypeCode },
		},
		actions: {
			setTemplateIdToDelete,
			setSelectedTemplateId,
			setSelectedAssetId,
			setSelectedTitle,
			setSelectedCraftId,
			setSelectedDownHours,
			setSelectedIsNextDownDay,
			setSelectedPlannedHours,
			setSelectedDowntimeClassCode,
			setSelectedNumberOfWorkers,
			setSelectedDescription,
			setSelectedExpectedShifts,
			setSelectedWarningShifts,
			setSelectedShiftsPerDay,
			setSelectedPeriodDays,
			setSelectedWarningDays,
			setSelectedExpectedDays,
			setSelectedAssetName,
			setSelectedSchedulingPolicyCode,
			setSelectedScheduleStart,
			setFirstTime,
			setShowDownDay,
			setRowSelectedTemplate,
			setSelectedCronExpression,
		},
	} = useTemplatesContext();

	const navigate = useNavigate();
	const [searchParams, setSearchParams] = useSearchParams();
	const gridRef = useRef<AgGridReact>(null);
	const gridStyle = useMemo(() => ({ height: "100%", width: "100%", paddingBottom: 60, boxSizing: "border-box" as const }), []);
	const [error, setError] = useState<string>("");
	const [isParametersLoading, setIsParametersLoading] = useState<boolean>(true);
	const [parametersError, setParametersError] = useState<string>("");
	const [createPMOrderError, setCreatePMOrderError] = useState<string>("");
	const [deleteTemplateError, setDeleteTemplateError] = useState<string>("");

	useLoadPeriodPerDay({ setIsParametersLoading, setParametersError });

	const { templates, isLoading, error: listError } = useTemplates({
		assetId: searchAssetIdToTable,
		searchText,
		showDownDay,
	});

	const queryClient = useQueryClient();
	const refreshTemplates = useCallback(
		() => queryClient.invalidateQueries({ queryKey: TEMPLATES_QUERY_KEYS.all }),
		[queryClient],
	);

	const { deleteTemplate } = useDeleteTemplate({
		onSuccess: () => {
			setopenModalDelete(false);
			setDeleteSuccess(true);
		},
		onError: (e) => setDeleteTemplateError(e.message),
	});
	const { createPMOrder } = useCreatePMOrder({
		onSuccess: () => setOnDemandModal(false),
		onError: (e) => {
			setCreatePMOrderError(e.message);
			setOnDemandModal(false);
		},
	});

	useEffect(() => {
		if (listError) setError(listError.message);
	}, [listError]);

	const [modalCreateActive, setModalCreateActive] = useState(false);
	const [modalEditActive, setModalEditActive] = useState(false);
	const [onDemandModal, setOnDemandModal] = useState(false);
	const [openModalDelete, setopenModalDelete] = useState<boolean>(false);
	const [deleteSuccess, setDeleteSuccess] = useState<boolean>(false);
	const [openTemplateResources, setOpenTemplateResources] = useState(false);
	const [newMode, setNewMode] = useState(false);

	const openResourceModal = useCallback(
		(rowData: any) => {
			setRowSelectedTemplate(rowData);
			setOpenTemplateResources(true);
		},
		[setRowSelectedTemplate],
	);

	const customPeriodRenderer = (params: ICellRendererParams) => {
		const { data } = params;
		const schedulingPolicyCode = data.SchedulingPolicyCode;
		if (schedulingPolicyCode === "P") {
			return "Periodic";
		} else if (schedulingPolicyCode === "D") {
			return "Down days";
		} else {
			return data.PeriodInDays;
		}
	};

	const onActionsClick = (
		e: React.MouseEvent<HTMLButtonElement, MouseEvent>,
		params: EditableCallbackParams,
	) => {
		(params.api as any).showContextMenu({
			column: params.column,
			rowNode: params.node,
			undefined,
			mouseEvent: e.nativeEvent,
		});
	};

	const renderCategoryCell = (props: ICellRendererParams) => {
		return (
			<div
				style={{
					height: 32,
					display: "flex",
					justifyContent: "center",
					alignItems: "center",
				}}
			>
				<div
					style={{
						height: 26,
						width: 26,
						borderRadius: 4,
						display: "flex",
						justifyContent: "center",
						alignItems: "center",
						background: props.data?.CraftColor || "#FFFFFF",
					}}
				>
					<div style={{ color: "#FFF", fontWeight: 600 }}>
						<div>{getCraftLetter(props.data?.CraftName, props.data?.CraftCode)}</div>
					</div>
				</div>
			</div>
		);
	};

	const AssetNameColumn = ({ data }: EditableCallbackParams) => {
		return <div title={data?.FullPath}>{data?.AssetName}</div>;
	};

	const [columnDefs] = useState<ColDef[]>([
		{
			cellRenderer: AssetNameColumn,
			headerName: "Asset",
			minWidth: 290,
			flex: 3,
		},
		{
			field: "Title",
			headerName: "Title",
			minWidth: 130,
			flex: 4,
		},
		{
			field: "CraftId",
			headerName: "Craft",
			minWidth: 80,
			flex: 1,
			cellRenderer: renderCategoryCell,
		},
		{
			field: "PeriodInDays",
			headerName: "Period (days)",
			minWidth: 120,
			flex: 1,
			cellRenderer: customPeriodRenderer,
		},
		{
			field: "PlannedHours",
			headerName: "Planned Hours",
			maxWidth: 120,
			flex: 1,
		},
		{
			field: "NumberOfWorkers",
			headerName: "# Of Workers",
			minWidth: 120,
			flex: 1,
		},
		{
			field: "DownDayCode",
			headerName: "Down Day",
			minWidth: 60,
			maxWidth: 60,
			flex: 1,
			cellRenderer: (params: ICellRendererParams) =>
				params.value ? (
					<Tooltip
						title={params.value === "N" ? "Next down day" : "Down day"}
					>
						<span
							style={{
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								width: "100%",
								height: "100%",
							}}
						>
							<ScheduleIcon
								fontSize="small"
								sx={{ color: params.value === "N" ? "error.main" : "#1976d2" }}
							/>
						</span>
					</Tooltip>
				) : (
					""
				),
		},
		{
			headerName: "",
			flex: 1,
			maxWidth: 80,
			cellClass: "ag-columns",
			cellRenderer: (params: EditableCallbackParams) => {
				return (
					<Grid2
						container
						style={{
							height: "100%",
							display: "flex",
							justifyContent: "center",
							alignItems: "center",
						}}
					>
						<Grid2
							style={{
								display: "flex",
								justifyContent: "center",
								alignItems: "center",
							}}
							size={{ xs: 12, md: 12 }}
						>
							<Grid2
								container
								spacing={1}
								style={{ justifyContent: "flex-end", minWidth: 30 }}
							>
								<Grid2 size={{ xs: 12, md: 12 }}>
									<Button fullWidth onClick={(e) => onActionsClick(e, params)}>
										<FormatListBulletedSharpIcon
											style={{ height: "auto" }}
											color="action"
										/>
									</Button>
								</Grid2>
							</Grid2>
						</Grid2>
					</Grid2>
				);
			},
		},
	]);

	const handleClose = (
		event: Event | React.SyntheticEvent,
		reason?: string,
	) => {
		if (reason === "clickaway") {
			return;
		}
		setDeleteSuccess(false);
	};

	const loadingOverlayComponent = useMemo<any>(() => {
		return CustomLoadingOverlay;
	}, []);

	const getContextMenuItems = useCallback(
		(params: GetContextMenuItemsParams): (DefaultMenuItem | MenuItemDef)[] => {
			const rowSelected = params.node?.data;
			rowSelected && setRowSelectedTemplate(rowSelected);
			const result: (DefaultMenuItem | MenuItemDef)[] = [
				{
					name: "New Template",
					action: () => {
						setModalCreateActive(true);
					},
					icon: '<i class="fas fa-plus"></i>',
				},
			];
			if (params.node?.data) {
				result.push({
					name: "Asset Details",
					action: () => {
						navigate(
							`/asset-details/${params.node?.data?.AssetId}?resource=attachments&assets=true&statistics=true&templates=true`,
						);
					},
					icon: '<i class="fas fa-file-alt"></i>',
				});
			}
			// swv-cmms only uses Attachments
			result.push({
				name: "Template Attachments",
				action: () => openResourceModal(params.node?.data),
				icon: '<i class="fas fa-paperclip"></i>',
			});

			if (params.node?.data) {
				result.push({
					name: "Edit Template",
					action: () => {
						setSelectedTemplateId(params.node?.data.TemplateId),
							setSelectedAssetId(params.node?.data.AssetId),
							setSelectedAssetName(params.node?.data.AssetName),
							setSelectedTitle(params.node?.data.Title),
							setSelectedCraftId(params.node?.data.CraftId),
							setSelectedDownHours(params.node?.data.DownHours),
							setSelectedIsNextDownDay(params.node?.data.IsNextDownDay),
							setSelectedPlannedHours(params.node?.data.PlannedHours),
							setSelectedDowntimeClassCode(params.node?.data.DowntimeClassCode),
							setSelectedNumberOfWorkers(params.node?.data.NumberOfWorkers),
							setSelectedDescription(params.node?.data.Description),
							setSelectedExpectedShifts(params.node?.data.ExpectedShifts),
							setSelectedWarningShifts(params.node?.data.WarningShifts),
							setSelectedShiftsPerDay(params.node?.data.shiftsPerDay),
							setSelectedPeriodDays(params.node?.data.PeriodShifts),
							setSelectedWarningDays(params.node?.data.WarningShifts),
							setSelectedExpectedDays(params.node?.data.ExpectedShifts),
							setSelectedSchedulingPolicyCode(
								params.node?.data.SchedulingPolicyCode,
							),
							setSelectedCronExpression(params.node?.data.CronExpression),
							setSelectedScheduleStart(params.node?.data.ScheduleStart);
						setModalEditActive(true);
					},
					icon: '<i class="fas fa-edit"></i>',
				});
			}
			if (params.node?.data) {
				result.push({
					name: "Delete Template",
					action: () => {
						setopenModalDelete(true);
						setTemplateIdToDelete(rowSelected.TemplateId);
					},
					icon: '<i class="fas fa-trash"></i>',
				});
			}
			if (params.node?.data) {
				result.push("separator", {
					name: "Force Open Order",
					action: () => setOnDemandModal(true),
					icon: '<i class="far fa-plus-square"></i>',
				});
			}
			return result;
		},
		[templates],
	);

	const onDobleClicked = () => {
		const selectedRow = gridRef.current!.api.getSelectedNodes();
		setRowSelectedTemplate(selectedRow[0].data);
		setSelectedTemplateId(selectedRow[0].data.TemplateId),
			setSelectedAssetId(selectedRow[0].data.AssetId),
			setSelectedAssetName(selectedRow[0].data.AssetName),
			setSelectedTitle(selectedRow[0].data.Title),
			setSelectedCraftId(selectedRow[0].data.CraftId),
			setSelectedDownHours(selectedRow[0].data.DownHours),
			setSelectedIsNextDownDay(selectedRow[0].data.IsNextDownDay),
			setSelectedPlannedHours(selectedRow[0].data.PlannedHours),
			setSelectedDowntimeClassCode(selectedRow[0].data.DowntimeClassCode),
			setSelectedNumberOfWorkers(selectedRow[0].data.NumberOfWorkers),
			setSelectedDescription(selectedRow[0].data.Description),
			setSelectedExpectedShifts(selectedRow[0].data.ExpectedShifts),
			setSelectedWarningShifts(selectedRow[0].data.WarningShifts),
			setSelectedShiftsPerDay(selectedRow[0].data.shiftsPerDay),
			setSelectedPeriodDays(selectedRow[0].data.PeriodShifts),
			setSelectedWarningDays(selectedRow[0].data.WarningShifts),
			setSelectedExpectedDays(selectedRow[0].data.ExpectedShifts),
			setSelectedSchedulingPolicyCode(selectedRow[0].data.SchedulingPolicyCode),
			setSelectedScheduleStart(selectedRow[0].data.ScheduleStart),
			setSelectedCronExpression(selectedRow[0].data.CronExpression),
			setModalEditActive(true);
	};

	const handleCreatePMorder = () => {
		createPMOrder(rowSelectedTemplate.TemplateId as number);
	};

	const changeSearchAsset = (value: number | null) => {
		navigate(`/templates/${value}`, { replace: true });
	};

	const deleteDetail = (rowSelected: number) => {
		deleteTemplate(rowSelected);
	};

	const resetFilter = () => {
		setShowDownDay(true);
		searchParams.delete("search");
		setSearchParams(searchParams, { replace: true });

		setTimeout(() => {
			setFirstTime(true);
			navigate(`/templates/${defaultAreaId}`, { replace: true });
		}, 500);
	};

	useInitializeTemplate();
	useSearchParamsToReducer();

	useEffect(() => {
		const templateId = searchParams.get("templateId");
		const templateIdNum = templateId ? Number.parseInt(templateId) : null;
		const templateRow = templateIdNum
			? templates.find((row: Template) => row.TemplateId === templateIdNum)
			: null;

		if (templateRow && gridRef.current) {
			gridRef.current.api.forEachNode((node) => {
				if (node.data?.TemplateId === templateIdNum) {
					node.setSelected(true);
					gridRef.current!.api.ensureNodeVisible(node);
					setRowSelectedTemplate(templateRow);
				}
			});
		}
	}, [searchParams, templates, setRowSelectedTemplate]);

	return (
		<>
			{openTemplateResources && (
				<TableTemplateResoures
					show={openTemplateResources}
					onHide={(shouldUpdate: boolean) => {
						setOpenTemplateResources(false);
						if (shouldUpdate) {
							refreshTemplates();
						}
					}}
					AssetId={AssetId}
					TemplateId={TemplateId}
					Title={Title}
					AssetName={AssetName as string}
					TypeCode={TypeCode as string}
				/>
			)}
			<Grid2
				container
				style={{
					padding: "10px 20px",
					height: "calc(100vh - 64px)",
					boxSizing: "border-box",
					flexDirection: "column",
					flexWrap: "nowrap",
				}}
				onContextMenu={(e) => e.preventDefault()}
			>
				<Grid2
					container
					size={{ xs: 12 }}
					alignItems="center"
					justifyContent="center"
					spacing={1}
					style={{ flexBasis: "auto", flexShrink: 0, width: "100%" }}
				>
					<Grid2 size={{ md: 12, xs: 12 }}>
						<Typography
							variant="h5"
							style={{
								margin: "0",
								fontWeight: 600,
								userSelect: "none",
								marginLeft: 5,
							}}
						>
							TEMPLATES
						</Typography>
					</Grid2>

					<Grid2 size={{ xs: 12 }}>
						<Paper elevation={1} style={{ padding: 10, marginBottom: 20 }}>
							<Grid2 container spacing={2} alignItems="center">
								<Grid2 size={{ lg: 3, md: 3, sm: 12, xs: 12 }}>
									<AssetTreePicker
										label="Asset"
										value={Number.parseInt(searchAssetIdToTable as string) || 0}
										onChange={changeSearchAsset}
									/>
								</Grid2>

								<Grid2 size={{ lg: 7, md: 7, sm: 12, xs: 12 }}>
									<Filters />
								</Grid2>

								<Grid2 size={{ lg: 2, md: 2, sm: 12, xs: 12 }}>
									<Button
										fullWidth
										variant="contained"
										color="inherit"
										onClick={resetFilter}
										size="medium"
									>
										RESET
									</Button>
								</Grid2>
							</Grid2>
						</Paper>
					</Grid2>
				</Grid2>

				<Paper
					elevation={1}
					style={{
						width: "100%",
						padding: 10,
						borderRadius: "0.75rem",
						flex: 1,
						minHeight: 0,
						boxSizing: "border-box",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Grid2
						style={{ position: "relative", flex: 1, minHeight: 0, width: "100%" }}
						size={{ md: 12, xs: 12 }}
					>
						<div style={gridStyle}>
							<AgGridReact
								ref={gridRef}
								rowData={templates}
								columnDefs={columnDefs}
								defaultColDef={globalDefaultColDef}
								rowHeight={35}
								headerHeight={32}
								animateRows={true}
								loadingOverlayComponent={loadingOverlayComponent}
								getContextMenuItems={getContextMenuItems}
								rowSelection="single"
								onRowDoubleClicked={onDobleClicked}
								loading={isLoading}
							/>
							<IconButton
								onClick={() => {
									setModalCreateActive(!modalCreateActive);
								}}
								aria-label="add"
								color="primary"
								style={{
									padding: 0,
									position: "absolute",
									left: 10,
									bottom: 10,
								}}
							>
								<AddBoxIcon style={{ width: 35, height: 35 }} />
							</IconButton>
						</div>
					</Grid2>
				</Paper>
			</Grid2>

			<CreateTemplate
				show={modalCreateActive}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshTemplates();
						setModalCreateActive(false);
					} else setModalCreateActive(false);
				}}
			/>
			<EditTemplate
				show={modalEditActive}
				onHide={(shouldUpdate: boolean) => {
					if (shouldUpdate) {
						refreshTemplates();
						setModalEditActive(false);
					} else setModalEditActive(false);
				}}
				data={rowSelectedTemplate}
				initialNewMode={newMode}
			/>

			<Dialog open={openModalDelete} aria-labelledby="responsive-dialog-title">
				<DialogTitle id="responsive-dialog-title">WARNING</DialogTitle>
				<DialogContent>
					<DialogContentText style={{ padding: 0, margin: 0 }}>
						Be careful, once deleted you will not be able to recover this
						template, are you sure you want to delete it?
					</DialogContentText>
				</DialogContent>
				<DialogActions style={{ padding: "16px 24px" }}>
					<Button
						onClick={() => setopenModalDelete(false)}
						autoFocus
						color="inherit"
					>
						CANCEL
					</Button>
					<Button
						autoFocus
						color="secondary"
						onClick={() => deleteDetail(templateIdToDelete!)}
					>
						Delete
					</Button>
				</DialogActions>
			</Dialog>

			<Dialog open={onDemandModal} aria-labelledby="responsive-dialog-title">
				<DialogTitle id="responsive-dialog-title">NEW WORK ORDER</DialogTitle>
				<DialogContent style={{ padding: "0 24px" }}>
					<Grid2 container style={{ width: 400 }}>
						<Grid2 size={{ md: 12, xs: 12 }}>
							Title: {rowSelectedTemplate.Title}
						</Grid2>
						<Grid2 size={{ md: 12, xs: 12 }}>
							Asset: {rowSelectedTemplate.AssetName}
						</Grid2>
					</Grid2>
					<DialogContentText style={{ padding: 0, marginTop: 10 }}>
						Are you sure you want open a new Work Order?
					</DialogContentText>
				</DialogContent>
				<DialogActions style={{ padding: "0px 24px 18px" }}>
					<Button
						onClick={() => setOnDemandModal(false)}
						autoFocus
						color="inherit"
					>
						CANCEL
					</Button>
					<Button
						autoFocus
						color="primary"
						onClick={handleCreatePMorder}
						variant="contained"
					>
						OPEN
					</Button>
				</DialogActions>
			</Dialog>

			<Snackbar
				open={deleteSuccess}
				autoHideDuration={2500}
				onClose={handleClose}
				anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
			>
				<Alert severity="error" onClose={handleClose}>
					Deleted
				</Alert>
			</Snackbar>

			<ErrorModal error={error} onHide={() => setError("")} />
			<ErrorModal
				error={parametersError}
				onHide={() => setParametersError("")}
			/>
			<ErrorModal
				error={createPMOrderError}
				onHide={() => setCreatePMOrderError("")}
			/>
			<ErrorModal
				error={deleteTemplateError}
				onHide={() => setDeleteTemplateError("")}
			/>
		</>
	);
};
